import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Grid, Paper, Typography, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, TextField,
  ToggleButtonGroup, ToggleButton, Button, Chip, InputAdornment,
  IconButton, Divider
} from '@mui/material';
import {
  Clear as ClearIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { get } from '../../services/apiService';
import SnykIncomingTrendChart from './SnykIncomingTrendChart';
import SnykIncomingBySeverityChart from './SnykIncomingBySeverityChart';
import SnykIncomingByMicroserviceChart from './SnykIncomingByMicroserviceChart';
import SnykIssuesTable from './SnykIssuesTable';

const DATE_PRESETS = [
  { label: 'Last 30d', days: 30 },
  { label: 'Last 90d', days: 90 },
  { label: 'Last 6mo', days: 180 },
  { label: 'Last 1yr', days: 365 },
  { label: 'Custom',   days: null }
];

const SEVERITIES = ['Critical', 'High', 'Medium', 'Low'];
const SEVERITY_CHIP_COLORS = { Critical: 'error', High: 'warning', Medium: 'info', Low: 'success' };
const GRANULARITIES = ['daily', 'weekly', 'monthly', 'quarterly'];

const toDateStr = (d) => d.toISOString().split('T')[0];

const defaultFilters = () => {
  const to = new Date();
  const from = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  return { from: toDateStr(from), to: toDateStr(to), granularity: 'daily', severity: '', microservice: '', library: '' };
};

// Convert a period label back to a date range based on granularity
const periodToDateRange = (period, granularity) => {
  if (granularity === 'daily') {
    return { from: period, to: period };
  }
  if (granularity === 'monthly') {
    const [year, month] = period.split('-').map(Number);
    const from = new Date(year, month - 1, 1);
    const to = new Date(year, month, 0);
    return { from: toDateStr(from), to: toDateStr(to) };
  }
  if (granularity === 'quarterly') {
    const [yearStr, qStr] = period.split('-Q');
    const year = Number(yearStr);
    const q = Number(qStr);
    const startMonth = (q - 1) * 3;
    const from = new Date(year, startMonth, 1);
    const to = new Date(year, startMonth + 3, 0);
    return { from: toDateStr(from), to: toDateStr(to) };
  }
  if (granularity === 'weekly') {
    const match = period.match(/^(\d{4})-W(\d+)$/);
    if (match) {
      const year = Number(match[1]);
      const week = Number(match[2]);
      const jan4 = new Date(year, 0, 4);
      const dayOfWeek = jan4.getDay() || 7;
      const week1Monday = new Date(jan4.getTime() - (dayOfWeek - 1) * 86400000);
      const weekStart = new Date(week1Monday.getTime() + (week - 1) * 7 * 86400000);
      const weekEnd = new Date(weekStart.getTime() + 6 * 86400000);
      return { from: toDateStr(weekStart), to: toDateStr(weekEnd) };
    }
  }
  return { from: period, to: period };
};

const HorizontalBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1, border: '1px solid #ccc' }}>
      <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-all' }}>{label}</Typography>
      <Typography variant="body2">{payload[0]?.value} issues</Typography>
    </Paper>
  );
};

const SnykAnalyticsDashboard = () => {
  const [filters, setFilters] = useState(defaultFilters());
  const [preset, setPreset] = useState('Last 30d');
  const [data, setData] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ microservices: [], libraries: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [librarySearch, setLibrarySearch] = useState('');
  const [drillDown, setDrillDown] = useState(null); // { period, severity, introducedFrom, introducedTo }

  const drillDownRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        from: filters.from,
        to: filters.to,
        granularity: filters.granularity
      });
      if (filters.severity)     params.append('severity', filters.severity);
      if (filters.microservice) params.append('microservice', filters.microservice);
      if (filters.library)      params.append('library', filters.library);

      const response = await get(`/api/snyk/analytics?${params}`);
      if (response?.success) {
        setData(response.data);
        if (response.filterOptions) setFilterOptions(response.filterOptions);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Clear drill-down when main filters change
  useEffect(() => { setDrillDown(null); }, [filters]);

  const applyPreset = (label) => {
    setPreset(label);
    const found = DATE_PRESETS.find(p => p.label === label);
    if (found?.days) {
      const to = new Date();
      const from = new Date(Date.now() - found.days * 24 * 60 * 60 * 1000);
      setFilters(f => ({ ...f, from: toDateStr(from), to: toDateStr(to) }));
    }
  };

  const handleClear = () => {
    setPreset('Last 30d');
    setLibrarySearch('');
    setDrillDown(null);
    setFilters(defaultFilters());
  };

  const applyLibrarySearch = () => setFilters(f => ({ ...f, library: librarySearch.trim() }));
  const handleLibraryKeyDown = (e) => { if (e.key === 'Enter') applyLibrarySearch(); };
  const clearLibrary = () => { setLibrarySearch(''); setFilters(f => ({ ...f, library: '' })); };

  const handleSeverityBarClick = ({ period, severity }) => {
    const { from, to } = periodToDateRange(period, filters.granularity);
    setDrillDown({ period, severity, introducedFrom: from, introducedTo: to });
    setTimeout(() => drillDownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  return (
    <Box p={3}>
      {/* Filter Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">

          <Grid item xs={12} md="auto">
            <ToggleButtonGroup value={preset} exclusive onChange={(_, v) => v && applyPreset(v)} size="small">
              {DATE_PRESETS.map(p => (
                <ToggleButton key={p.label} value={p.label}>{p.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>

          {preset === 'Custom' && (
            <>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="From" type="date" size="small" fullWidth
                  value={filters.from}
                  onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="To" type="date" size="small" fullWidth
                  value={filters.to}
                  onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Granularity</InputLabel>
              <Select value={filters.granularity} label="Granularity" onChange={e => setFilters(f => ({ ...f, granularity: e.target.value }))}>
                {GRANULARITIES.map(g => (
                  <MenuItem key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Severity</InputLabel>
              <Select value={filters.severity} label="Severity" onChange={e => setFilters(f => ({ ...f, severity: e.target.value }))}>
                <MenuItem value="">All</MenuItem>
                {SEVERITIES.map(s => (
                  <MenuItem key={s} value={s}>
                    <Chip label={s} color={SEVERITY_CHIP_COLORS[s]} size="small" sx={{ mr: 1 }} />{s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Microservice</InputLabel>
              <Select value={filters.microservice} label="Microservice" onChange={e => setFilters(f => ({ ...f, microservice: e.target.value }))}>
                <MenuItem value="">All Microservices</MenuItem>
                {filterOptions.microservices.map(m => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="Library (e.g. tomcat, spring)"
              size="small" fullWidth
              value={librarySearch}
              onChange={e => setLibrarySearch(e.target.value)}
              onKeyDown={handleLibraryKeyDown}
              onBlur={applyLibrarySearch}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: librarySearch ? (
                  <InputAdornment position="end">
                    <ClearIcon fontSize="small" sx={{ cursor: 'pointer' }} onClick={clearLibrary} />
                  </InputAdornment>
                ) : null
              }}
            />
          </Grid>

          <Grid item xs="auto">
            <Button variant="outlined" size="small" startIcon={<ClearIcon />} onClick={handleClear}>
              Clear
            </Button>
          </Grid>
        </Grid>

        {(filters.microservice || filters.library) && (
          <Box display="flex" gap={1} mt={1.5} flexWrap="wrap">
            {filters.microservice && (
              <Chip label={`Microservice: ${filters.microservice}`} size="small" onDelete={() => setFilters(f => ({ ...f, microservice: '' }))} />
            )}
            {filters.library && (
              <Chip label={`Library: ${filters.library}`} size="small" onDelete={clearLibrary} />
            )}
          </Box>
        )}
      </Paper>

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && data && (
        <Grid container spacing={3}>

          {/* Chart 1: Incoming trend overall */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom align="center">
                Incoming Vulnerabilities Over Time
              </Typography>
              <SnykIncomingTrendChart data={data.incomingTrend} />
            </Paper>
          </Grid>

          {/* Chart 2: Incoming by severity — clickable for drill-down */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom align="center">
                Incoming by Severity
              </Typography>
              <SnykIncomingBySeverityChart
                data={data.incomingBySeverity}
                onBarClick={handleSeverityBarClick}
              />
            </Paper>
          </Grid>

          {/* Chart 3: Incoming by microservice (stacked trend) */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom align="center">
                Incoming by Microservice (Top 8)
              </Typography>
              <SnykIncomingByMicroserviceChart
                data={data.incomingByMicroservice}
                serviceNames={data.topMicroserviceNames}
              />
            </Paper>
          </Grid>

          {/* Chart 4 & 5: Top microservices / libraries (horizontal bar) */}
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom align="center">
                Top Microservices by New Issues
              </Typography>
              {!data.topMicroservices?.length ? (
                <Box display="flex" alignItems="center" justifyContent="center" height={320}>
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={data.topMicroservices} layout="vertical" margin={{ top: 5, right: 40, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} tickFormatter={v => v.length > 22 ? `${v.slice(0, 22)}…` : v} />
                    <Tooltip content={<HorizontalBarTooltip />} />
                    <Bar dataKey="count" name="Issues" fill="#1976d2" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 11 }} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom align="center">
                Top Libraries by New Issues
              </Typography>
              {!data.topLibraries?.length ? (
                <Box display="flex" alignItems="center" justifyContent="center" height={320}>
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={data.topLibraries} layout="vertical" margin={{ top: 5, right: 40, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} tickFormatter={v => v.length > 22 ? `${v.slice(0, 22)}…` : v} />
                    <Tooltip content={<HorizontalBarTooltip />} />
                    <Bar dataKey="count" name="Issues" fill="#7b1fa2" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 11 }} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

        </Grid>
      )}

      {/* Drill-down table — shown when a severity bar is clicked */}
      {drillDown && (
        <Box ref={drillDownRef} mt={4}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <FilterIcon color="primary" />
                <Typography variant="h6">
                  Drill-down: Issues introduced in&nbsp;
                  <strong>{drillDown.period}</strong>
                </Typography>
                <Chip
                  label={drillDown.severity}
                  color={SEVERITY_CHIP_COLORS[drillDown.severity]}
                  size="small"
                />
                <Typography variant="body2" color="text.secondary">
                  ({drillDown.introducedFrom}{drillDown.introducedFrom !== drillDown.introducedTo ? ` → ${drillDown.introducedTo}` : ''})
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setDrillDown(null)} title="Close drill-down">
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <SnykIssuesTable
              filters={{
                severity: drillDown.severity,
                state: 'all',
                introducedFrom: drillDown.introducedFrom,
                introducedTo: drillDown.introducedTo
              }}
              collection=""
            />
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default SnykAnalyticsDashboard;
